import { useMutation, useQueryClient } from '@tanstack/react-query'
import type React from 'react'
import { useParams } from 'react-router-dom'
import { FileUploadModal } from '../../../components/FileUploadModal'
import type { FileUploadRequest } from '../../../components/FileUploadModal/types'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'

/**
 * @fileoverview eNPS External Participants Upload Modal
 *
 * Modal for uploading external participants via CSV/Excel files.
 * Supports column mapping for email, firstName, lastName, and optional company.
 */

const ALLOWED_FILE_TYPES = [
	'application/vnd.ms-excel',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'text/csv',
]

const FILE_EXTENSIONS = '.csv, .xls, .xlsx'
const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2MB

interface EnpsExternalParticipantsUploadModalProps {
	isOpen: boolean
	onClose: () => void
	onUploadSuccess: () => void
	enpsConfigId: string
}

export const EnpsExternalParticipantsUploadModal: React.FC<EnpsExternalParticipantsUploadModalProps> = ({
	isOpen,
	onClose,
	onUploadSuccess,
	enpsConfigId,
}) => {
	const { organizationId } = useParams<{ organizationId: string }>()
	const queryClient = useQueryClient()
	const toast = useToast()

	const uploadRequestMutation = useMutation({
		...trpc.admin.enpsFiles.requestFileUpload.mutationOptions(),
	})

	const acknowledgeUploadMutation = useMutation({
		...trpc.admin.enpsFiles.acknowledgeFileUpload.mutationOptions(),
	})

	const processFileMutation = useMutation({
		...trpc.admin.enpsFiles.processFile.mutationOptions({
			onSuccess: (data) => {
				if (data.skipped > 0) {
					toast.showToast(`Uploaded ${data.created} participants (${data.skipped} duplicates skipped)`)
				} else {
					toast.showToast(`Successfully uploaded ${data.created} external participants`)
				}
			},
			onError: () => {
				toast.showToast('Error uploading external participants file', 'error')
			},
			onSettled: () => {
				// Invalidate the config external participants query
				queryClient.invalidateQueries({
					queryKey: trpc.admin.enpsFiles.getConfigExternalParticipants.queryKey({
						enpsConfigId,
						organizationId: organizationId as string,
					}),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	})

	const uploadRequest: FileUploadRequest = {
		requestUpload: (data) =>
			uploadRequestMutation.mutateAsync({
				...data,
				enpsConfigId,
				organizationId: organizationId as string,
			}),
		acknowledgeUpload: (data) =>
			acknowledgeUploadMutation.mutateAsync({
				...data,
				enpsConfigId,
			}),
		processFile: async (data) => {
			await processFileMutation.mutateAsync({
				fileId: data.fileId,
				enpsConfigId,
				organizationId: organizationId as string,
				emailHeader: data.columnMapping.emailHeader,
				firstNameHeader: data.columnMapping.firstNameHeader,
				lastNameHeader: data.columnMapping.lastNameHeader,
				companyHeader: data.columnMapping.companyHeader || undefined,
			})
		},
	}

	const handleClose = () => {
		onClose()
	}

	return (
		<FileUploadModal
			isOpen={isOpen}
			onClose={handleClose}
			onUploadSuccess={onUploadSuccess}
			fileConfig={{
				allowedTypes: ALLOWED_FILE_TYPES,
				extensions: FILE_EXTENSIONS,
				maxSize: MAX_FILE_SIZE,
			}}
			columnMappingFields={[
				{
					key: 'emailHeader',
					label: 'Email Column',
					required: true,
				},
				{
					key: 'firstNameHeader',
					label: 'First Name Column',
					required: true,
				},
				{
					key: 'lastNameHeader',
					label: 'Last Name Column',
					required: true,
				},
				{
					key: 'companyHeader',
					label: 'Company Column (optional)',
					required: false,
				},
			]}
			uploadRequest={uploadRequest}
			title='Upload External Participants'
			overwriteMessage='Duplicate emails will be skipped. Participants will be saved immediately.'
		/>
	)
}
